import { toLocalISODate } from "@/utilities";

// ─────────────────────────────────────────────────────────────────────────
// Sample portfolio data — DESIGN TESTING ONLY.
//
// Flip the switch below to render the dashboard hero, the Portfolio page and
// the earnings-breakdown modal from the fixture in this file instead of the
// signed-in investor's records. Set it back to false to go live — nothing
// else needs changing, and no other file hard-codes portfolio numbers.
//
// The fixture is a set of RAW RECORDS in the shape the API returns
// (investments / distributions / bankaccounts), NOT pre-computed display
// values. Every KPI still runs through the same calculation path as
// production data, so sample mode exercises the real math rather than
// bypassing it — if a number looks wrong here, it is wrong in production too.
// ─────────────────────────────────────────────────────────────────────────
// export const USE_SAMPLE_PORTFOLIO_DATA = true;
export const USE_SAMPLE_PORTFOLIO_DATA = false;

// Single seam every portfolio surface reads through. Pass the data store;
// get back either its live records or the fixture.
export function portfolioDataSource(dataStore) {
  if (USE_SAMPLE_PORTFOLIO_DATA) {
    return {
      investments: SAMPLE_INVESTMENTS,
      distributions: SAMPLE_DISTRIBUTIONS,
      bank_accounts: SAMPLE_BANK_ACCOUNTS,
    };
  }
  return {
    investments: dataStore.investments ?? [],
    distributions: dataStore.distributions ?? [],
    bank_accounts: dataStore.bank_accounts ?? [],
  };
}

// ── Fixture definition ───────────────────────────────────────────────────
// Day of the month the back office distributes on.
const PAYMENT_DAY = 10;
// How many months of not-yet-paid distributions to schedule ahead. Only the
// earliest feeds "next earnings"; the rest just make the fixture realistic.
const UPCOMING_MONTHS = 3;
const SAMPLE_USER_ID = "sample-user";

export const SAMPLE_BANK_ACCOUNTS = [
  {
    record_id: "sample-bank-4453",
    user_id: SAMPLE_USER_ID,
    financial_institution_name: "Wells Fargo",
    account_number: "000000004453",
    routing_number: "121000248",
    account_type: "Checking",
    institution_id: "ins_4",
    plaid_account_id: "sample-plaid-4453",
    created_date: toLocalISODate(monthsFromToday(-8)),
  },
  {
    record_id: "sample-bank-3356",
    user_id: SAMPLE_USER_ID,
    financial_institution_name: "PNC",
    account_number: "000000003356",
    routing_number: "043000096",
    account_type: "Checking",
    institution_id: "ins_13",
    plaid_account_id: "sample-plaid-3356",
    created_date: toLocalISODate(monthsFromToday(-2)),
  },
];

// One entry per sample bond; everything else (payment schedule, accrued
// interest, current NAV, lifetime totals) is generated from these. Months held
// are counted back from today, so the fixture never goes stale.
//
// Coverage the account-card layouts depend on:
//   • Personal and IRA each hold a compounding *and* a monthly-paying bond, so
//     their cards render all three sublines.
//   • Joint and Entity are compounding-only, so their cards drop the
//     "Monthly payments" subline.
//   • Four account types total, which is the tallest hero layout. Comment out
//     entries to test the collapsed single-account-type layout.
const SAMPLE_BONDS = [
  {
    recordId: "sample-inv-1120",
    type: "Individual",
    amount: 25000,
    interestRate: 13,
    termMonths: 60,
    monthsHeld: 20,
    isCompounded: true,
    offeringType: "Reg D",
  },
  {
    recordId: "sample-inv-4456",
    type: "Individual",
    amount: 15000,
    interestRate: 11,
    termMonths: 36,
    monthsHeld: 26,
    isCompounded: false,
    offeringType: "S1",
    distributionAccount: "sample-bank-4453",
  },
  {
    recordId: "sample-inv-2318",
    type: "Joint",
    amount: 30000,
    interestRate: 12,
    termMonths: 48,
    monthsHeld: 14,
    isCompounded: true,
    offeringType: "Reg D",
  },
  {
    recordId: "sample-inv-5532",
    type: "Traditional IRA",
    amount: 70000,
    interestRate: 13,
    termMonths: 60,
    monthsHeld: 18,
    isCompounded: true,
    offeringType: "Reg D",
    // Gives the Portfolio "Rollovers available" tab something to show.
    ableToRollover: true,
    rolloverAvailable: 12000,
  },
  {
    recordId: "sample-inv-7741",
    type: "Traditional IRA",
    amount: 30000,
    interestRate: 12,
    termMonths: 36,
    monthsHeld: 12,
    isCompounded: false,
    offeringType: "Reg A",
    distributionAccount: "sample-bank-3356",
  },
  {
    recordId: "sample-inv-9843",
    type: "Entity",
    amount: 30000,
    interestRate: 11,
    termMonths: 24,
    monthsHeld: 6,
    isCompounded: true,
    offeringType: "S1",
  },
];

function roundCents(value) {
  return Math.round(value * 100) / 100;
}

function monthsFromToday(months) {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth() + months, today.getDate());
}

// Safe to add months this way because every date built here sits on
// PAYMENT_DAY (10), which exists in every month — no end-of-month rollover.
function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

// The next distribution date that has not been paid yet. A distribution dated
// today still counts as upcoming — it is processed during the day.
function nextPaymentDate() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), PAYMENT_DAY);
  return thisMonth >= today ? thisMonth : addMonths(thisMonth, 1);
}

function buildDistribution(bond, date, amount, isCompleted) {
  const distributionDate = toLocalISODate(date);
  return {
    record_id: `sample-dist-${bond.recordId}-${distributionDate}`,
    user_id: SAMPLE_USER_ID,
    investment_id: bond.recordId,
    distribution_date: distributionDate,
    amount,
    is_compounded: bond.isCompounded,
    is_completed: isCompleted,
    is_title_transfer: false,
  };
}

// Walks each bond's payment schedule month by month, oldest first, so a
// compounding bond's balance rolls forward exactly the way the back office
// accrues it and current_nav always equals principal + paid accruals.
function buildSampleRecords() {
  const nextPayment = nextPaymentDate();
  const currentYear = new Date().getFullYear();
  const investments = [];
  const distributions = [];

  for (const bond of SAMPLE_BONDS) {
    const monthlyRate = bond.interestRate / 100 / 12;
    const firstPaymentDate = addMonths(nextPayment, -bond.monthsHeld);

    let balance = bond.amount;
    let interestPaidToDate = 0;
    let interestPaidThisYear = 0;

    for (let monthsBack = bond.monthsHeld; monthsBack >= 1; monthsBack--) {
      const date = addMonths(nextPayment, -monthsBack);
      const amount = roundCents(balance * monthlyRate);
      if (bond.isCompounded) balance = roundCents(balance + amount);
      interestPaidToDate = roundCents(interestPaidToDate + amount);
      if (date.getFullYear() === currentYear) {
        interestPaidThisYear = roundCents(interestPaidThisYear + amount);
      }
      distributions.push(buildDistribution(bond, date, amount, true));
    }

    // current_nav is the balance as of the last *completed* payment; the
    // scheduled months below must not move it.
    const currentNav = balance;

    let scheduledBalance = balance;
    for (let monthsAhead = 0; monthsAhead < UPCOMING_MONTHS; monthsAhead++) {
      const date = addMonths(nextPayment, monthsAhead);
      const amount = roundCents(scheduledBalance * monthlyRate);
      if (bond.isCompounded) scheduledBalance = roundCents(scheduledBalance + amount);
      distributions.push(buildDistribution(bond, date, amount, false));
    }

    investments.push({
      record_id: bond.recordId,
      user_id: SAMPLE_USER_ID,
      type: bond.type,
      status: "Settled",
      amount: bond.amount,
      current_nav: currentNav,
      bonds: bond.amount / 1000,
      is_compounded: bond.isCompounded,
      interest_rate: bond.interestRate,
      bond_term: bond.termMonths,
      offering_type: bond.offeringType,
      is_short_term: false,
      opt_out_decision_complete: true,
      signature_date: toLocalISODate(addMonths(firstPaymentDate, -1)),
      first_payment_date: toLocalISODate(firstPaymentDate),
      last_payment_date: toLocalISODate(addMonths(firstPaymentDate, bond.termMonths)),
      maturity_date: toLocalISODate(addMonths(firstPaymentDate, bond.termMonths)),
      distribution_method: bond.isCompounded ? "Compounding Bonds" : "ACH",
      distribution_account: bond.distributionAccount ?? null,
      inception_to_date_interest_paid: interestPaidToDate,
      year_to_date_interest_paid: interestPaidThisYear,
      able_to_rollover: bond.ableToRollover ?? false,
      rollover_available: bond.rolloverAvailable ?? 0,
      paid_out: false,
      user_requested_payout: false,
    });
  }

  return { investments, distributions };
}

const sampleRecords = buildSampleRecords();

export const SAMPLE_INVESTMENTS = sampleRecords.investments;
export const SAMPLE_DISTRIBUTIONS = sampleRecords.distributions;
